/** Default GA4 measurement ID (prod). Override with REACT_APP_GA_MEASUREMENT_ID. */
export const DEFAULT_GA_MEASUREMENT_ID = "G-ZY3X8H6PYR";

export const TOOL_ACTION_EVENT = "tool_action";

export type AnalyticsEventParams = Record<
  string,
  string | number | boolean | undefined | null
>;

function getMeasurementId(): string {
  const fromEnv = process.env.REACT_APP_GA_MEASUREMENT_ID?.trim();
  return fromEnv || DEFAULT_GA_MEASUREMENT_ID;
}

function isAnalyticsDisabled(): boolean {
  return process.env.REACT_APP_GA_DISABLE === "true";
}

function canTrack(): boolean {
  if (isAnalyticsDisabled()) return false;
  const id = getMeasurementId();
  if (!id) return false;
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

function sanitizeParams(
  params?: AnalyticsEventParams
): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    out[key] = value;
  }
  return out;
}

export function trackPageView(opts: {
  path: string;
  title?: string;
  tool?: string;
}): void {
  if (!canTrack()) return;
  const measurementId = getMeasurementId();
  const pagePath = opts.path || "/";

  window.gtag!("config", measurementId, {
    page_path: pagePath,
    page_title: opts.title || document.title,
    send_page_view: false,
  });

  window.gtag!("event", "page_view", {
    page_path: pagePath,
    page_title: opts.title || document.title,
    tool_name: opts.tool || "Unknown",
  });
}

export function trackEvent(
  name: string,
  params?: AnalyticsEventParams
): void {
  if (!canTrack()) return;
  window.gtag!("event", name, sanitizeParams(params));
}

/**
 * Track a primary tool CTA. Prefer this over raw trackEvent for action coverage.
 */
export function trackToolAction(
  toolName: string,
  action: string,
  extra?: AnalyticsEventParams
): void {
  const pagePath =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : undefined;

  trackEvent(TOOL_ACTION_EVENT, {
    tool_name: toolName,
    action,
    page_path: pagePath,
    ...extra,
  });
}
